import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import colosseumVisual from "../../../assets/images/colosseum/image.png";
import { createReferral } from "../../../helpers/api/referral";
import { updateUserIdentity, type AuthenticatedUser } from "../../../helpers/api/user";
import { connectWallet } from "../../../helpers/auth/authentication";
import { getWalletConnector } from "../../../helpers/auth/walletConnector";
import { useAuthStore } from "../../../store/useAuthStore";

type HomeStep = "connect" | "install" | "profile";
type ProfileField = "username" | "email";

const USERNAME_PATTERN = /^[A-Za-z0-9_]{5,31}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readAuthUser = (): AuthenticatedUser | null => {
  const storedUser = sessionStorage.getItem("authUser");
  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser) as AuthenticatedUser;
  } catch {
    return null;
  }
};

const needsProfile = (user: AuthenticatedUser | null) =>
  Boolean(user && (user.email === null || user.username === null));

const shortenWallet = (walletAddress?: string | null) => {
  if (!walletAddress) return "";
  return `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
};

const Home = () => {
  document.title = "Legends | The Colosseum";

  const navigate = useNavigate();
  const { username: referrerUsername } = useParams<{ username?: string }>();
  const setUser = useAuthStore((state) => state.setUser);
  const walletConnector = useMemo(() => getWalletConnector(), []);
  const [hasMetaMask, setHasMetaMask] = useState(false);
  const [step, setStep] = useState<HomeStep>("install");
  const [authUser, setAuthUser] = useState<AuthenticatedUser | null>(() => readAuthUser());
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [touchedFields, setTouchedFields] = useState<Record<ProfileField, boolean>>({
    username: false,
    email: false,
  });
  const [acceptRules, setAcceptRules] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const currentYear = new Date().getFullYear();
  const isUsernameValid = USERNAME_PATTERN.test(username.trim());
  const isEmailValid = EMAIL_PATTERN.test(email.trim());
  const isProfileFormValid = isUsernameValid && isEmailValid;
  const isUsernameInvalid = touchedFields.username && !isUsernameValid;
  const isEmailInvalid = touchedFields.email && !isEmailValid;

  const markFieldTouched = (field: ProfileField) => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
  };

  useEffect(() => {
    const installed = walletConnector.isMetaMaskInstalled();
    const currentUser = readAuthUser();

    setHasMetaMask(installed);
    setAuthUser(currentUser);

    if (needsProfile(currentUser)) {
      setStep("profile");
      return;
    }

    setStep(installed ? "connect" : "install");
  }, [walletConnector]);

  useEffect(() => {
    document.body.classList.add("home");
    return () => {
      document.body.classList.remove("home");
    };
  }, []);

  const handleConnectWallet = async () => {
    setErrorMessage("");
    setIsConnecting(true);

    try {
      await connectWallet();
      const connectedUser = readAuthUser();

      if (!connectedUser) {
        throw new Error("Wallet authentication failed. Please reconnect MetaMask.");
      }

      if (referrerUsername) {
        await createReferral({
          referrerUsername,
          referee: connectedUser.id,
        });
      }

      setAuthUser(connectedUser);

      if (needsProfile(connectedUser)) {
        setStep("profile");
        return;
      }

      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Wallet connection failed. Please approve MetaMask and retry."
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRetryMetaMask = () => {
    const installed = walletConnector.isMetaMaskInstalled();
    setHasMetaMask(installed);
    setStep(installed ? "connect" : "install");
  };

  const handleSaveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");

    if (!authUser) {
      setErrorMessage("Your wallet session has expired. Please reconnect MetaMask.");
      setStep(hasMetaMask ? "connect" : "install");
      return;
    }

    if (!acceptRules) {
      setErrorMessage("You must accept the Terms and the Fair-play Charter.");
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedUsername = username.trim();

    if (!isProfileFormValid) {
      setTouchedFields({ username: true, email: true });
      return;
    }

    setIsSavingProfile(true);
    try {
      const { user } = await updateUserIdentity(authUser.id, {
        email: normalizedEmail,
        username: normalizedUsername,
      });
      const updatedUser = { ...authUser, ...user };

      sessionStorage.setItem("authUser", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setAuthUser(updatedUser);
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Profile update failed. Check your email, username, or try another username."
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const renderBrandPanel = () => {
    const isProfileStep = step === "profile";

    return (
      <aside className="arena-home__brand" aria-label="The Colosseum">
        <img className="arena-home__image" src={colosseumVisual} alt="" aria-hidden="true" />
        <div className="arena-home__shade" />
        <div className="arena-home__brand-content">
          <h1>
            {isProfileStep ? (
              <>
                One last
                <span>step.</span>
              </>
            ) : (
              <>
                Enter the
                <span>arena.</span>
              </>
            )}
          </h1>
          <p>
            {isProfileStep
              ? "Username + email. We ask only once on your first login."
              : "Bet points on legendary duels. The pool swells. Winners split the loot."}
          </p>
          {isProfileStep && (
            <div className="arena-home__progress" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          )}
          <small>{isProfileStep ? "STEP 2 - PROFILE" : `V1.0 - ${currentYear} - EN`}</small>
        </div>
      </aside>
    );
  };

  const renderConnectPanel = () => (
    <section className="arena-home__panel" aria-labelledby="connect-title">
      <p className="arena-home__eyebrow">STEP 1 - CONNECT</p>
      <h2 id="connect-title">Connect with MetaMask</h2>
      <p className="arena-home__copy">Your wallet is your identity - no password, no tracking.</p>

      <div className="arena-home__wallet-card">
        <div className="arena-home__wallet-icon">M</div>
        <div>
          <strong>MetaMask</strong>
          <span className="arena-home__detected">Detected in browser</span>
        </div>
      </div>

      <button className="arena-home__primary" type="button" onClick={handleConnectWallet} disabled={isConnecting}>
        {isConnecting ? "Connecting..." : "Connect MetaMask ->"}
      </button>
      <p className="arena-home__hint">The wallet opens a popup - you approve</p>
      {errorMessage && <div className="arena-home__error">{errorMessage}</div>}
    </section>
  );

  const renderInstallPanel = () => (
    <section className="arena-home__panel" aria-labelledby="install-title">
      <p className="arena-home__eyebrow arena-home__eyebrow--warning">NO WALLET DETECTED</p>
      <h2 id="install-title">Install MetaMask to continue</h2>
      <p className="arena-home__copy">
        No wallet was found in your browser. Install the extension then come back to this page - the connection will
        happen automatically.
      </p>

      <div className="arena-home__install-card">
        <div className="arena-home__wallet-card arena-home__wallet-card--missing">
          <div className="arena-home__wallet-icon arena-home__wallet-icon--missing" />
          <div>
            <strong>MetaMask</strong>
            <span className="arena-home__missing">Not found</span>
          </div>
        </div>
        <Link className="arena-home__primary" to="https://metamask.io/download" target="_blank" rel="noopener noreferrer">
          Install MetaMask
        </Link>
        <p className="arena-home__hint">External link - metamask.io/download</p>
      </div>

      <ol className="arena-home__steps">
        <li>Click Install (new tab).</li>
        <li>Add the extension and create or import a wallet.</li>
        <li>Refresh - we detect and unlock the connection.</li>
      </ol>

      <button className="arena-home__secondary" type="button" onClick={handleRetryMetaMask}>
        I have installed it - retry
      </button>
      <p className="arena-home__hint">Polls window.ethereum on click</p>
    </section>
  );

  const renderProfilePanel = () => (
    <section className="arena-home__panel arena-home__panel--profile" aria-labelledby="profile-title">
      <div className="arena-home__connected">
        <span>Wallet connected</span>
        <strong>{shortenWallet(authUser?.walletAddress)}</strong>
      </div>

      <h2 id="profile-title">Claim your name</h2>
      <form onSubmit={handleSaveProfile} noValidate>
        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          minLength={5}
          maxLength={31}
          placeholder="Spartacus_42"
          required
          pattern="[A-Za-z0-9_]{5,31}"
          aria-invalid={isUsernameInvalid}
          className={isUsernameInvalid ? "arena-home__input--invalid" : undefined}
          onBlur={() => markFieldTouched("username")}
          onChange={(event) => setUsername(event.target.value)}
        />
        <small>5-31 chars - letters, numbers, _</small>

        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          value={email}
          placeholder="champion@colosseum.app"
          required
          aria-invalid={isEmailInvalid}
          className={isEmailInvalid ? "arena-home__input--invalid" : undefined}
          onBlur={() => markFieldTouched("email")}
          onChange={(event) => setEmail(event.target.value)}
        />
        <small>Only for battle results & alerts</small>

        <label className="arena-home__check">
          <input
            type="checkbox"
            checked={acceptRules}
            onChange={(event) => setAcceptRules(event.target.checked)}
          />
          <span>
            I accept the <Link to="/terms">Terms</Link> and the <Link to="/fair-play">Fair-play Charter</Link>
          </span>
        </label>

        {errorMessage && <div className="arena-home__error">{errorMessage}</div>}
        <button className="arena-home__primary" type="submit" disabled={isSavingProfile || !isProfileFormValid}>
          {isSavingProfile ? "Saving..." : "Claim my 10,000 pts ->"}
        </button>
        <p className="arena-home__hint">Shown only on first login - next -&gt; dashboard</p>
      </form>
    </section>
  );

  return (
    <main className="arena-home">
      {renderBrandPanel()}
      <div className="arena-home__right">
        {step === "install" && renderInstallPanel()}
        {step === "connect" && renderConnectPanel()}
        {step === "profile" && renderProfilePanel()}
      </div>

    </main>
  );
};

export default Home;
